<template>
  <el-dialog
    v-model="visible"
    :title="dialogTitle"
    width="920px"
    top="4vh"
    destroy-on-close
    append-to-body
    class="answer-test-dialog"
    @closed="handleClosed"
  >
    <div v-loading="loadingInit" class="answer-test-body">
      <section class="answer-test-section answer-test-section--full">
        <div class="section-label row-between">
          <span>测试问题（韩文原文）</span>
          <button
            type="button"
            class="text-action-btn"
            :disabled="loadingLatestQuestion"
            @click="fillLatestQuestion"
          >
            {{ loadingLatestQuestion ? '加载中...' : '最近一条' }}
          </button>
        </div>
        <el-input
          v-model="question"
          type="textarea"
          :rows="4"
          placeholder="输入测谎问题，例如：메이플 프레넛의 최대 레벨은?"
        />
      </section>

      <section class="answer-test-section answer-test-section--full answer-test-ai-config">
        <div class="section-label row-between">
          <span>AI 配置</span>
          <div class="section-actions">
            <span v-if="aiConfigEdited" class="section-hint">已手动修改</span>
            <button type="button" class="text-action-btn" @click="resetAiConfig">重置</button>
          </div>
        </div>
        <div class="ai-config-grid">
          <div class="ai-config-item">
            <span class="ai-config-label">AI 选择</span>
            <el-radio-group v-model="aiProvider" size="small" @change="onAiProviderChange">
              <el-radio
                v-for="item in AI_PROVIDER_OPTIONS"
                :key="item.value"
                :label="item.value"
              >
                {{ item.label }}
              </el-radio>
            </el-radio-group>
          </div>
          <div class="ai-config-item">
            <span class="ai-config-label">模型</span>
            <el-select
              v-if="aiProvider === AI_PROVIDER_CLAUDE"
              v-model="model"
              class="model-select"
              size="small"
              @change="aiConfigEdited = true"
            >
              <el-option
                v-for="item in CLAUDE_MODEL_OPTIONS"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
            <ArkModelSelect
              v-else
              v-model="model"
              size="small"
              select-class="model-select"
              input-class="model-input"
              control-width="240px"
              @update:model-value="aiConfigEdited = true"
            />
          </div>
          <div class="ai-config-item ai-config-item--full">
            <span class="ai-config-label">API Key</span>
            <el-input
              v-model="apiKey"
              class="api-key-input"
              size="small"
              show-password
              clearable
              placeholder="默认取自整体设置，可临时修改用于测试"
              @input="aiConfigEdited = true"
            />
          </div>
        </div>
        <p class="prompt-edit-hint">可临时修改用于测试，不会保存到整体设置</p>
      </section>

      <section class="answer-test-section answer-test-section--full">
        <div class="section-label row-between">
          <span>Prompt 预览</span>
          <div class="section-actions">
            <span v-if="promptEdited" class="section-hint">已手动修改</span>
            <button type="button" class="text-action-btn" @click="resetTestPrompt">重新生成</button>
          </div>
        </div>
        <el-input
          v-model="testPrompt"
          type="textarea"
          :rows="16"
          class="prompt-preview"
          @input="promptEdited = true"
        />
        <p class="prompt-edit-hint">可临时修改用于测试，不会保存到整体设置</p>
      </section>

      <section class="answer-test-section answer-test-controls answer-test-section--full">
        <div class="control-block">
          <div class="section-label">测试轮数</div>
          <el-radio-group v-model="rounds" class="round-radio-group">
            <el-radio-button v-for="n in roundOptions" :key="n" :label="n">
              ×{{ n }}
            </el-radio-button>
          </el-radio-group>
        </div>

        <div class="control-actions">
          <el-button type="primary" :loading="testing" @click="startTest">
            {{ testing ? `测试中 ${finishedRounds}/${rounds}` : '开始测试' }}
          </el-button>
          <el-button v-if="testing" @click="cancelTest">停止</el-button>
        </div>
      </section>

      <section class="answer-test-results">
        <div class="section-label row-between">
          <span>测试结果</span>
          <span v-if="testResults.length" class="section-hint">共 {{ testResults.length }} 轮</span>
        </div>
        <div ref="resultsScrollRef" class="results-log">
          <div v-if="!testResults.length" class="results-empty">点击「开始测试」后，每轮回答与耗时会实时显示在这里</div>
          <div
            v-for="item in testResults"
            :key="item.round"
            class="result-item"
            :class="`result-item--${item.status}`"
            :title="item.answer || ''"
          >
            <span class="result-round">第{{ item.round }}轮</span>
            <span class="result-duration">{{ formatDuration(item.durationMs) }}</span>
            <span class="result-answer">
              {{ item.answer || (item.status === 'running' ? '请求中...' : '-') }}
            </span>
            <span v-if="getAnswerLength(item.answer) != null" class="result-length">
              {{ getAnswerLength(item.answer) }}字
            </span>
          </div>
        </div>
      </section>
    </div>
  </el-dialog>
</template>

<script>
import { computed, nextTick, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import answerTestAPI from '../api/answerTest'
import answerRecordAPI from '../api/answerRecord'
import { getGlobalConfig } from '../api/configs'
import questionBankAPI from '../api/questionBank'
import { GM_TRAIN_LIE_DEFAULT_PROMPT, AI_PROVIDER_OPTIONS, AI_PROVIDER_CLAUDE, AI_PROVIDER_ARK, CLAUDE_MODEL_OPTIONS, ARK_MODEL_OPTIONS, defaultGmTrainLieConfig, ensureGmTrainLieConfig, getGmTrainLieProviderConfig } from '../constants/gmTrainLiePrompt'
import ArkModelSelect from './ArkModelSelect.vue'
import {
  buildGmTrainLiePrompt,
  formatQuestionBankLines,
  getRoleAnswerTestContext
} from '../utils/buildGmTrainLiePrompt'

const ROUND_OPTIONS = [1, 3, 5, 10, 20, 50]

export default {
  name: 'AnswerTestDialog',
  components: { ArkModelSelect },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    role: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val)
    })

    const loadingInit = ref(false)
    const loadingLatestQuestion = ref(false)
    const testing = ref(false)
    const cancelled = ref(false)
    const question = ref('')
    const promptTemplate = ref(GM_TRAIN_LIE_DEFAULT_PROMPT)
    const gmConfig = ref(defaultGmTrainLieConfig())
    const aiProvider = ref(AI_PROVIDER_CLAUDE)
    const apiKey = ref('')
    const model = ref(CLAUDE_MODEL_OPTIONS[0])
    const questionBankItems = ref([])
    const rounds = ref(1)
    const testResults = ref([])
    const finishedRounds = ref(0)
    const resultsScrollRef = ref(null)
    const testPrompt = ref('')
    const promptEdited = ref(false)
    const aiConfigEdited = ref(false)

    const roundOptions = ROUND_OPTIONS

    const syncProviderFields = () => {
      const cfg = getGmTrainLieProviderConfig(gmConfig.value, aiProvider.value)
      apiKey.value = cfg.api_key || ''
      model.value = cfg.model || (aiProvider.value === AI_PROVIDER_ARK ? ARK_MODEL_OPTIONS[0] : CLAUDE_MODEL_OPTIONS[0])
    }

    const onAiProviderChange = () => {
      syncProviderFields()
      aiConfigEdited.value = true
    }

    const resetAiConfig = () => {
      aiProvider.value = gmConfig.value.ai_provider || AI_PROVIDER_CLAUDE
      syncProviderFields()
      aiConfigEdited.value = false
    }

    const roleContext = computed(() => getRoleAnswerTestContext(props.role))

    const questionBankLines = computed(() => formatQuestionBankLines(questionBankItems.value))

    const generatedPrompt = computed(() =>
      buildGmTrainLiePrompt(promptTemplate.value, {
        ...roleContext.value,
        question: question.value,
        questionBankLines: questionBankLines.value
      })
    )

    watch(generatedPrompt, (value) => {
      if (!promptEdited.value) {
        testPrompt.value = value
      }
    }, { immediate: true })

    const resetTestPrompt = () => {
      promptEdited.value = false
      testPrompt.value = generatedPrompt.value
    }

    const dialogTitle = computed(() => {
      const role = props.role
      if (!role) return '答题测试'
      const windowId = role.window_id || '-'
      const roleName = role.name || roleContext.value.roleName || '-'
      return `答题测试 · 窗口 ${windowId} · ${roleName}`
    })

    const scrollResultsToBottom = async () => {
      await nextTick()
      const el = resultsScrollRef.value
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    }

    const loadInitData = async () => {
      loadingInit.value = true
      testResults.value = []
      finishedRounds.value = 0
      cancelled.value = false
      promptEdited.value = false
      aiConfigEdited.value = false
      try {
        const [globalRes, bankRes] = await Promise.all([
          getGlobalConfig(),
          questionBankAPI.getAll()
        ])
        if (globalRes.code === 200 && globalRes.data?.['GM训练测谎']) {
          gmConfig.value = ensureGmTrainLieConfig(globalRes.data['GM训练测谎'])
          promptTemplate.value = gmConfig.value.prompt || GM_TRAIN_LIE_DEFAULT_PROMPT
          aiProvider.value = gmConfig.value.ai_provider || AI_PROVIDER_CLAUDE
          syncProviderFields()
        }
        if (bankRes.code === 200) {
          questionBankItems.value = bankRes.data || []
        }
      } catch (e) {
        console.error(e)
        ElMessage.error('加载测试配置失败')
      } finally {
        loadingInit.value = false
      }
    }

    watch(
      () => props.modelValue,
      (open) => {
        if (open) {
          loadInitData()
        }
      }
    )

    const formatDuration = (ms) => {
      if (ms == null || ms < 0) return '-'
      if (ms < 1000) return `${ms}ms`
      return `${(ms / 1000).toFixed(2)}s`
    }

    const getAnswerLength = (answer) => {
      if (answer == null || answer === '') return null
      return String(answer).length
    }

    const cancelTest = () => {
      cancelled.value = true
    }

    const fillLatestQuestion = async () => {
      loadingLatestQuestion.value = true
      try {
        const windowId = props.role?.window_id
        const res = await answerRecordAPI.latest(windowId)
        if (res.code === 200 && res.data?.question) {
          question.value = res.data.question
          ElMessage.success('已填入最近一条答题记录的问题')
        } else {
          ElMessage.warning(res.message || '暂无答题记录')
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || e.message || '获取最近问题失败')
      } finally {
        loadingLatestQuestion.value = false
      }
    }

    const startTest = async () => {
      if (testing.value) return
      if (!String(question.value).trim()) {
        ElMessage.warning('请输入测试问题')
        return
      }
      if (!String(apiKey.value).trim()) {
        ElMessage.warning('请配置 API Key')
        return
      }
      if (!String(model.value).trim()) {
        ElMessage.warning('请配置模型')
        return
      }

      if (!String(testPrompt.value).trim()) {
        ElMessage.warning('Prompt 不能为空')
        return
      }

      testing.value = true
      cancelled.value = false
      testResults.value = []
      finishedRounds.value = 0

      for (let i = 1; i <= rounds.value; i += 1) {
        if (cancelled.value) break

        const row = {
          round: i,
          status: 'running',
          answer: '',
          durationMs: 0
        }
        testResults.value.push(row)
        await scrollResultsToBottom()

        const startedAt = Date.now()
        try {
          const res = await answerTestAPI.chat({
            ai_provider: aiProvider.value,
            api_key: apiKey.value.trim(),
            model: model.value.trim(),
            user_prompt: testPrompt.value.trim()
          })
          row.durationMs = res.data?.duration_ms ?? Date.now() - startedAt
          row.answer = res.data?.answer || ''
          row.status = 'success'
        } catch (e) {
          row.durationMs = Date.now() - startedAt
          row.answer = e.response?.data?.message || e.message || '请求失败'
          row.status = 'error'
        }

        finishedRounds.value = i
        await scrollResultsToBottom()
      }

      if (cancelled.value) {
        const last = testResults.value[testResults.value.length - 1]
        if (last && last.status === 'running') {
          last.status = 'cancelled'
          last.answer = '已手动停止'
        }
      }

      testing.value = false
    }

    const handleClosed = () => {
      cancelled.value = true
      testing.value = false
    }

    return {
      visible,
      loadingInit,
      loadingLatestQuestion,
      testing,
      question,
      aiProvider,
      apiKey,
      model,
      AI_PROVIDER_OPTIONS,
      AI_PROVIDER_CLAUDE,
      CLAUDE_MODEL_OPTIONS,
      aiConfigEdited,
      resetAiConfig,
      onAiProviderChange,
      rounds,
      roundOptions,
      testPrompt,
      promptEdited,
      resetTestPrompt,
      testResults,
      finishedRounds,
      resultsScrollRef,
      dialogTitle,
      formatDuration,
      getAnswerLength,
      startTest,
      cancelTest,
      fillLatestQuestion,
      handleClosed
    }
  }
}
</script>

<style scoped>
.answer-test-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.answer-test-section {
  min-width: 0;
}

.answer-test-section--full {
  width: 100%;
}

.answer-test-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  align-items: flex-end;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.answer-test-ai-config {
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.ai-config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.ai-config-item {
  min-width: 0;
}

.ai-config-item--full {
  grid-column: 1 / -1;
}

.ai-config-item .ai-config-label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
}

.model-input,
.api-key-input {
  width: 100%;
}

.model-select {
  width: 240px;
  max-width: 100%;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.prompt-edit-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
}

.control-block {
  min-width: 200px;
}

.round-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.control-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.row-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.section-hint {
  font-size: 12px;
  font-weight: 400;
  color: #9ca3af;
}

.text-action-btn {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: #007aff;
  cursor: pointer;
  transition: color 0.15s ease;
}

.text-action-btn:hover:not(:disabled) {
  color: #0056b3;
  text-decoration: underline;
}

.text-action-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.answer-test-results {
  display: flex;
  flex-direction: column;
}

.results-log {
  min-height: 160px;
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #0f172a;
  padding: 10px;
}

.results-empty {
  color: #94a3b8;
  font-size: 13px;
  padding: 8px;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 6px;
  background: #1e293b;
  border: 1px solid #334155;
  font-size: 12px;
  line-height: 1.4;
  min-width: 0;
}

.result-item--running {
  border-color: #3b82f6;
}

.result-item--success {
  border-color: #22c55e;
}

.result-item--error {
  border-color: #ef4444;
}

.result-item--cancelled {
  border-color: #f59e0b;
}

.result-round {
  flex-shrink: 0;
  color: #e2e8f0;
  font-weight: 600;
}

.result-duration {
  flex-shrink: 0;
  color: #38bdf8;
  font-variant-numeric: tabular-nums;
}

.result-answer {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #f8fafc;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.result-length {
  flex-shrink: 0;
  color: #a5b4fc;
  font-variant-numeric: tabular-nums;
}

:deep(.prompt-preview .el-textarea__inner) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  background: #f8fafc;
  width: 100%;
}
</style>

<style>
.answer-test-dialog .el-dialog {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  margin-bottom: 0;
}

.answer-test-dialog .el-dialog__header {
  flex-shrink: 0;
}

.answer-test-dialog .el-dialog__body {
  padding-top: 8px;
  overflow-y: auto;
  max-height: calc(92vh - 54px);
  flex: 1;
  min-height: 0;
}
</style>
